import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { CourierDisclaimer, DisclaimerDestination } from '@app/shared/services/providers.service';

// At least one destination checkbox must be selected
const atLeastOneDestinationValidator = (group: AbstractControl): ValidationErrors | null => {
  const value = group.value || {};
  return Object.keys(value).some((key) => !!value[key]) ? null : { atLeastOneRequired: true };
};

@Component({
  standalone: false,
  selector: 'modal-edit-courier-disclaimers',
  templateUrl: './modal-edit-courier-disclaimers.component.html',
  styleUrls: ['./modal-edit-courier-disclaimers.component.scss'],
})
export class ModalEditCourierDisclaimersComponent implements OnInit {
  public provider: any;
  public providerName: string;
  public disclaimersForm: FormGroup;
  public destinationOptions: { key: DisclaimerDestination; label: string }[] = [
    { key: 'CA', label: 'Domestic (Canada)' },
    { key: 'US', label: 'United States' },
    { key: 'INT', label: 'International' },
  ];

  constructor(
    private dialogRef: MatDialogRef<ModalEditCourierDisclaimersComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      provider?: any;
    },
    private formBuilder: FormBuilder
  ) {
    this.provider = this.data.provider;
  }

  ngOnInit() {
    this.providerName = this?.provider?.provider;
    this.disclaimersForm = this.formBuilder.group({
      disclaimers: this.formBuilder.array([]),
    });
    (this.provider?.disclaimers || []).forEach((disclaimer: CourierDisclaimer) =>
      this.disclaimers.push(this.createDisclaimerFormGroup(disclaimer))
    );
  }

  // Disclaimers FormArray getter
  get disclaimers(): FormArray {
    return this.disclaimersForm.get('disclaimers') as FormArray;
  }

  // Create a new disclaimer FormGroup
  public createDisclaimerFormGroup(disclaimer?: CourierDisclaimer): FormGroup {
    const group = this.formBuilder.group({
      visible: [disclaimer ? disclaimer.visible : true],
      title: [disclaimer?.title || '', [Validators.required]],
      content: [disclaimer?.content || '', [Validators.required]],
      requireCheckbox: [disclaimer ? !!disclaimer.requireCheckbox : false],
      destinations: this.formBuilder.group(
        {
          CA: [!!disclaimer?.destinations?.includes('CA')],
          US: [!!disclaimer?.destinations?.includes('US')],
          INT: [!!disclaimer?.destinations?.includes('INT')],
        },
        { validators: [atLeastOneDestinationValidator] }
      ),
    });
    this.setDisclaimerFieldsState(group);
    return group;
  }

  // Add a new empty disclaimer
  public addDisclaimer(): void {
    this.disclaimers.push(this.createDisclaimerFormGroup());
  }

  // Remove a disclaimer at index (nothing persists until Save)
  public removeDisclaimer(index: number): void {
    this.disclaimers.removeAt(index);
  }

  // Hidden disclaimers are kept as drafts: gray out their fields and exempt them from validation
  public onVisibleToggled(index: number): void {
    this.setDisclaimerFieldsState(this.disclaimers.at(index) as FormGroup);
  }

  public save(): void {
    if (this.disclaimersForm.invalid) {
      this.disclaimersForm.markAllAsTouched();
      return;
    }

    const result: CourierDisclaimer[] = this.disclaimers.controls.map((control) => {
      const raw = (control as FormGroup).getRawValue();
      return {
        visible: !!raw.visible,
        title: (raw.title || '').trim(),
        content: (raw.content || '').trim(),
        requireCheckbox: !!raw.requireCheckbox,
        destinations: this.destinationOptions.map((option) => option.key).filter((key) => !!raw.destinations[key]),
      };
    });
    this.dialogRef.close(result);
  }

  private setDisclaimerFieldsState(group: FormGroup): void {
    const fields = ['title', 'content', 'requireCheckbox', 'destinations'];
    if (group.get('visible')?.value) {
      fields.forEach((field) => group.get(field)?.enable());
    } else {
      fields.forEach((field) => group.get(field)?.disable());
    }
  }
}
